/**
 * The colour legends for the weather time layers. Their tiles come already coloured, so
 * every scale here must match the gis-pipeline renderer that draws it:
 *
 *   radar                       registry/mrms.py (RAIN, SNOWC; HRRR future radar reuses them)
 *   temperature .. chance       registry/forecast.py (LAYERS)
 *   snow depth                  registry/snodas_tiles.py (CLASSES)
 *   drought                     registry/drought_tiles.py (STYLE)
 *
 * The tiles are classed, not smooth: each value range gets one flat colour. So a scale is
 * a list of classes, each with its colour, its tile alpha (0..255) and the value it starts
 * at. `from: null` is the open-ended class below the first edge, which only temperature
 * draws; every other layer leaves values below its first edge transparent.
 */

// Classes from (lower bounds, colours, alpha): one alpha for all, or one per class.
const classes = (from, colors, alpha) => colors.map((color, i) => ({
  color,
  alpha: Array.isArray(alpha) ? alpha[i] : alpha,
  from: from[i]
}))

export const WEATHER_LEGENDS = {
  radar: {
    caption: 'Radar reflectivity',
    unit: 'dBZ',
    rows: [
      {
        label: 'Rain',
        classes: classes([15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65],
          ['#A8F0A0', '#5FD85A', '#2DB52A', '#1E8C1E', '#F7E83B', '#F5B82E',
            '#F28A23', '#E8401C', '#C4161C', '#9E0F3A', '#D63AD6'], 200)
      },
      {
        label: 'Snow',
        classes: classes([5, 10, 15, 20, 25, 30, 35],
          ['#C9E4FF', '#9CCBFF', '#6FAEF5', '#4A8EE8', '#2F6BD1', '#1F4CB3', '#1A2F8F'], 200)
      }
    ]
  },
  temperature: {
    caption: 'Temperature',
    unit: '°F',
    rows: [{
      classes: classes([null, -20, -10, 0, 10, 20, 32, 40, 50, 60, 70, 80, 90, 100, 110],
        ['#7B3294', '#5E3C99', '#3F4FB0', '#2C7FB8', '#41B6C4', '#A1DAB4', '#D9F0A3', '#FFFFB2',
          '#FED976', '#FEB24C', '#FD8D3C', '#F03B20', '#BD0026', '#800026', '#4D0013'], 165)
    }]
  },
  wind: {
    caption: 'Wind speed',
    unit: 'mph',
    rows: [{
      classes: classes([5, 10, 15, 20, 25, 30, 40, 50],
        ['#C6DBEF', '#9ECAE1', '#6BAED6', '#4292C6', '#41AB5D', '#FEC44F', '#FE9929', '#E31A1C'], 170)
    }]
  },
  gusts: {
    caption: 'Wind gusts',
    unit: 'mph',
    rows: [{
      classes: classes([15, 20, 30, 40, 50, 60, 75],
        ['#FFFFB2', '#FED976', '#FEB24C', '#FD8D3C', '#F03B20', '#BD0026', '#7A0177'], 175)
    }]
  },
  cloud: {
    caption: 'Cloud cover',
    unit: '%',
    rows: [{
      classes: classes([10, 25, 50, 75, 90],
        ['#F7F7F7', '#D9D9D9', '#BDBDBD', '#969696', '#737373'], [60, 90, 120, 150, 180])
    }]
  },
  precip1h: {
    caption: '1-hour precipitation',
    unit: 'in',
    rows: [{
      classes: classes([0.01, 0.05, 0.1, 0.25, 0.5, 1, 2, 3],
        ['#CDEBC5', '#A1D99B', '#41AB5D', '#238B45', '#2B8CBE', '#0868AC', '#6A51A3', '#8E0152'], 190)
    }]
  },
  precip24h: {
    caption: '24-hour precipitation',
    unit: 'in',
    rows: [{
      classes: classes([0.1, 0.25, 0.5, 1, 1.5, 2, 3, 4],
        ['#CDEBC5', '#A1D99B', '#41AB5D', '#238B45', '#2B8CBE', '#0868AC', '#6A51A3', '#8E0152'], 190)
    }]
  },
  snowfall: {
    caption: '24-hour snowfall',
    unit: 'in',
    rows: [{
      classes: classes([0.1, 1, 2, 4, 6, 8, 12, 18, 24],
        ['#DCEBFA', '#B3D3F2', '#7FB2E5', '#4C8FD6', '#2A6BC2', '#1F4FA8', '#3B2F9E', '#5E2A9E', '#85249A'], 190)
    }]
  },
  chanceOfPrecip: {
    caption: '12-hour chance of precipitation',
    unit: '%',
    rows: [{
      classes: classes([10, 30, 50, 70, 90],
        ['#E5F5E0', '#A1D99B', '#41AB5D', '#238B45', '#00441B'], 170)
    }]
  },
  snowDepth: {
    caption: 'Snow depth',
    unit: 'in',
    rows: [{
      classes: classes([1, 2, 4, 8, 12, 18, 24, 36, 48, 72],
        ['#DCEBFA', '#B3D3F2', '#7FB2E5', '#4C8FD6', '#2A6BC2', '#1F4FA8', '#3B2F9E', '#5E2A9E', '#85249A', '#A51C7C'], 200)
    }]
  },
  // US Drought Monitor categories: named classes rather than value ranges.
  drought: {
    caption: 'US Drought Monitor',
    unit: null,
    rows: [{
      classes: [
        { color: '#FFFF00', alpha: 115, name: 'D0', title: 'Abnormally dry' },
        { color: '#FCD37F', alpha: 128, name: 'D1', title: 'Moderate drought' },
        { color: '#FFAA00', alpha: 128, name: 'D2', title: 'Severe drought' },
        { color: '#E60000', alpha: 128, name: 'D3', title: 'Extreme drought' },
        { color: '#730000', alpha: 140, name: 'D4', title: 'Exceptional drought' }
      ]
    }]
  }
}

/** The legend for a time layer kind (timeLayerKind's `kind`), or null for one without. */
export function weatherLegend(kind) {
  return WEATHER_LEGENDS[kind] || null
}

// The time bar's light, map-like ground under the classes' alpha.
export const LEGEND_GROUND = '#e6e4df'

/**
 * The same legend in lib/layerLegend's legendForDisplay shape, for the layer menu's (i)
 * popover: one ramp per row, titled with the row ("Rain") and the unit. Null for a kind
 * without one.
 */
export function weatherLegendForDisplay(kind) {
  const legend = weatherLegend(kind)
  if (!legend) return null
  return {
    single: false,
    swatches: legend.rows.map(row => ({
      shape: 'ramp',
      title: [row.label, legend.unit].filter(Boolean).join(' · ') || null,
      gradient: `${steppedGradient(row)}, ${LEGEND_GROUND}`,
      stops: thinned(legendTicks(row), POPOVER_MAX_TICKS)
    }))
  }
}

// The popover's bar is about half the time bar's width: past this, every other label.
const POPOVER_MAX_TICKS = 8

function thinned(ticks, max) {
  const every = Math.ceil(ticks.length / max)
  return ticks.filter((_, i) => i % every === 0)
}

/** '#A8F0A0' and 200 ->'rgba(168, 240, 160, 0.78)': a class's colour as the tile draws it. */
export function classColor({ color, alpha }) {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(color.slice(i, i + 2), 16))
  return `rgba(${r}, ${g}, ${b}, ${Math.round(alpha / 255 * 100) / 100})`
}

/** A stepped CSS gradient: each class one flat band of equal width, hard edges between. */
export function steppedGradient(row) {
  const n = row.classes.length
  const stops = row.classes.map((c, i) => {
    const color = classColor(c)
    return `${color} ${pct(i / n)}, ${color} ${pct((i + 1) / n)}`
  })
  return `linear-gradient(to right, ${stops.join(', ')})`
}

/**
 * The labels under a row's bar, each at its place along it (0..1). A value scale is
 * labelled at each class's lower bound, where the colour changes; a named scale (drought)
 * at the middle of each class.
 */
export function legendTicks(row) {
  const n = row.classes.length
  return row.classes
    .map((c, i) => (c.name != null
      ? { label: c.name, at: (i + 0.5) / n }
      : c.from == null ? null : { label: formatValue(c.from), at: i / n }))
    .filter(Boolean)
}

/** A screen reader's reading of a legend: "Temperature in °F: below −20, −20 to −10, ..., 110 and above". */
export function legendDescription(legend) {
  const rows = legend.rows.map(row => {
    const parts = row.classes.map((c, i) => {
      if (c.name != null) return `${c.name} ${c.title}`
      const next = row.classes[i + 1]
      if (c.from == null) return `below ${formatValue(next.from)}`
      if (!next) return `${formatValue(c.from)} and above`
      return `${formatValue(c.from)} to ${formatValue(next.from)}`
    })
    return `${row.label ? `${row.label}: ` : ''}${parts.join(', ')}`
  })
  return `${legend.caption}${legend.unit ? ` in ${legend.unit}` : ''}. ${rows.join('; ')}`
}

// -20 -> "−20" (a real minus), 0.01 -> "0.01".
function formatValue(v) {
  return v < 0 ? `−${-v}` : String(v)
}

function pct(f) {
  return `${Math.round(f * 10000) / 100}%`
}
